import React from 'react';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  color?: 'primary' | 'blue' | 'red' | 'green' | 'yellow';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  color = 'primary',
  size = 'md',
  className = '',
}) => {
  // Get toggle color classes
  const getColorClasses = () => {
    if (disabled) return 'bg-gray-300 dark:bg-gray-600';
    
    if (checked) {
      switch (color) {
        case 'blue':
          return 'bg-blue-500 dark:bg-blue-600';
        case 'red':
          return 'bg-red-500 dark:bg-red-600';
        case 'green':
          return 'bg-green-500 dark:bg-green-600';
        case 'yellow':
          return 'bg-yellow-500 dark:bg-yellow-600';
        case 'primary':
        default:
          return 'bg-red-500 dark:bg-red-600';
      }
    }
    
    return 'bg-gray-300 dark:bg-gray-700';
  };
  
  // Get toggle size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-8 h-4';
      case 'lg':
        return 'w-14 h-7';
      case 'md':
      default:
        return 'w-11 h-6';
    }
  };
  
  // Get thumb size and position classes
  const getThumbClasses = () => {
    let position = checked ? 'right-0.5' : 'left-0.5';
    
    switch (size) {
      case 'sm':
        return `h-3 w-3 ${position}`;
      case 'lg':
        return `h-6 w-6 ${position}`;
      case 'md':
      default:
        return `h-5 w-5 ${position}`;
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={`
        relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none
        focus:ring-2 focus:ring-red-500 focus:ring-offset-2
        ${getColorClasses()}
        ${getSizeClasses()}
        ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        ${className}
      `}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span
        className={`
          pointer-events-none absolute top-0.5 transform rounded-full bg-white shadow
          transition duration-200 ease-in-out
          ${getThumbClasses()}
        `}
      />
    </button>
  );
};

export default Toggle;